import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, Circle, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'mapbox-gl-leaflet';
import api from '../services/api';

const MAPBOX_TOKEN = 'pk.eyJ1IjoieW1hdWxhbmEiLCJhIjoiY210azNybGJ2MGg2YjJ3c2xpam82YW1yZSJ9.QbfabSASH3v_Lnb9bPFjag';

const MAPBOX_STYLES = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
  light: 'mapbox://styles/mapbox/light-v11',
  dark: 'mapbox://styles/mapbox/dark-v11',
};

function MapboxLayer({ styleName }) {
  const map = useMap();
  useEffect(() => {
    const gl = L.mapboxGL({ style: MAPBOX_STYLES[styleName], accessToken: MAPBOX_TOKEN }).addTo(map);
    return () => { map.removeLayer(gl); };
  }, [map, styleName]);
  return null;
}

const statusColor = {
  aktif: '#00843D',
  perlu_perhatian: '#E0A526',
  tidak_siap: '#D9362B',
  tidak_aktif: '#D9362B',
};

// Ikon marker berbentuk lingkaran warna status + emoji di tengah.
// Dibuat generik (terima emoji apa saja) supaya nanti gampang dipakai ulang utk P3K/APD/Hydrant.
const iconCache = {};
function getAssetIcon(emoji, color) {
  const key = emoji + color;
  if (iconCache[key]) return iconCache[key];
  const icon = L.divIcon({
    className: '',
    html: `<div style="background:${color};width:30px;height:30px;border-radius:50%;border:2.5px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;font-size:15px;line-height:1;">${emoji}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  });
  iconCache[key] = icon;
  return icon;
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 19);
      return;
    }
    const bounds = points.map(p => [p.lat, p.lng]);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 20 });
  }, [points, map]);
  return null;
}

export default function PetaGapCoverage() {
  const [garduList, setGarduList] = useState([]);
  const [selectedGI, setSelectedGI] = useState('');
  const [koordinatList, setKoordinatList] = useState([]);
  const [totalUnit, setTotalUnit] = useState(0);
  const [radius, setRadius] = useState(15);
  const [mapStyle, setMapStyle] = useState('streets'); // streets | satellite | light | dark
  const [isLoading, setIsLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({}); // { aparId: {lat, lng} }
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => { loadGarduList(); }, []);
  useEffect(() => { if (selectedGI) loadData(); }, [selectedGI]);

  const loadGarduList = async () => {
    try {
      const res = await api.get('/gardu');
      setGarduList(res.data.data || []);
      if (res.data.data?.length > 0) setSelectedGI(res.data.data[0].id);
    } catch (err) { console.error(err); }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [koordRes, daftarRes] = await Promise.all([
        api.get(`/apar/koordinat/${selectedGI}`),
        api.get(`/apar/daftar/${selectedGI}`),
      ]);
      setKoordinatList(koordRes.data.data || []);
      setTotalUnit((daftarRes.data.data || []).length);
    } catch (err) {
      console.error(err);
      setKoordinatList([]);
    }
    setIsLoading(false);
  };

  const handleDragEnd = (aparId, e) => {
    const { lat, lng } = e.target.getLatLng();
    setPendingChanges(prev => ({ ...prev, [aparId]: { lat, lng } }));
  };

  const simpanKoreksi = async () => {
    setIsSaving(true);
    try {
      await Promise.all(Object.entries(pendingChanges).map(([aparId, coord]) =>
        api.put(`/apar/koreksi-koordinat/${aparId}`, { latitude: coord.lat, longitude: coord.lng })
      ));
      setPendingChanges({});
      setEditMode(false);
      await loadData();
    } catch (err) {
      console.error(err);
      alert('Gagal menyimpan sebagian/semua koreksi. Coba lagi.');
    }
    setIsSaving(false);
  };

  const batalKoreksi = () => {
    setPendingChanges({});
    setEditMode(false);
  };

  const points = useMemo(() => koordinatList.map(k => ({
    id: k.id,
    lat: parseFloat(k.latitude),
    lng: parseFloat(k.longitude),
    kode: k.kode_apar,
    lokasi: k.lokasi_penempatan,
    status: k.status,
    akurasi: parseFloat(k.koordinat_akurasi_meter || 0),
  })), [koordinatList]);

  const namaGI = garduList.find(g => g.id == selectedGI)?.nama || '';
  const belumAdaKoordinat = totalUnit - points.length;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Peta Sebaran & Gap Coverage APAR</h1>
          <p className="text-sm text-gray-500 mt-1">Visualisasi radius jangkauan tiap APAR berdasarkan koordinat GPS hasil inspeksi</p>
        </div>
        {!editMode ? (
          <button onClick={() => setEditMode(true)}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50">
            ✏️ Koreksi Titik Manual
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={batalKoreksi}
              className="bg-white border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50">
              Batal
            </button>
            <button onClick={simpanKoreksi} disabled={isSaving || Object.keys(pendingChanges).length === 0}
              className="bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-600 disabled:opacity-40">
              {isSaving ? 'Menyimpan...' : `Simpan Koreksi (${Object.keys(pendingChanges).length})`}
            </button>
          </div>
        )}
      </div>
      {editMode && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-lg px-4 py-2 mb-4">
          Mode koreksi aktif — geser (drag) marker �� ke posisi yang benar berdasarkan citra satelit, lalu klik "Simpan Koreksi".
        </div>
      )}

      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-4 flex flex-wrap items-end gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Pilih Gardu Induk</label>
          <select value={selectedGI} onChange={(e) => setSelectedGI(e.target.value)}
            className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
            {garduList.map(gi => <option key={gi.id} value={gi.id}>{gi.nama}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Radius Jangkauan: <span className="font-bold text-green-700">{radius} meter</span>
          </label>
          <input type="range" min="5" max="30" value={radius} onChange={(e) => setRadius(Number(e.target.value))}
            className="w-full accent-green-700" />
          <p className="text-[11px] text-gray-400 mt-1">Sesuaikan dengan standar jarak maksimal APAR ke titik terjauh yang berlaku di unit Anda.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Gaya Peta</label>
          <select value={mapStyle} onChange={(e) => setMapStyle(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
            <option value="streets">Jalan (Streets)</option>
            <option value="satellite">Satelit + Label</option>
            <option value="light">Bersih Minimal (Light)</option>
            <option value="dark">Gelap (Dark)</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <p className="text-2xl font-bold text-gray-800">{totalUnit}</p>
          <p className="text-sm text-gray-500">Total Unit APAR</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <p className="text-2xl font-bold text-green-600">{points.length}</p>
          <p className="text-sm text-gray-500">Sudah Ada Koordinat</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <p className="text-2xl font-bold text-amber-500">{belumAdaKoordinat}</p>
          <p className="text-sm text-gray-500">Belum Ada Koordinat</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center" style={{ height: '520px' }}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : points.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-gray-400" style={{ height: '520px' }}>
            <p>Belum ada titik koordinat untuk {namaGI}.</p>
            <p className="text-xs mt-1">Koordinat terisi otomatis saat petugas inspeksi via scan QR.</p>
          </div>
        ) : (
          <MapContainer center={[points[0].lat, points[0].lng]} zoom={19} maxZoom={20} style={{ height: '520px', width: '100%' }}>
            <MapboxLayer styleName={mapStyle} />
            <FitBounds points={points} />
            {points.map((p, i) => {
              const color = statusColor[p.status] || '#9AA5A0';
              const pending = pendingChanges[p.id];
              const pos = pending ? [pending.lat, pending.lng] : [p.lat, p.lng];
              return (
                <React.Fragment key={i}>
                  <Circle center={pos} radius={radius} pathOptions={{ color, fillColor: color, fillOpacity: 0.15, weight: 1.5 }} />
                  <Marker
                    position={pos}
                    icon={getAssetIcon('\uD83E\uDDEF', pending ? '#2E6DA4' : color)}
                    draggable={editMode}
                    eventHandlers={editMode ? { dragend: (e) => handleDragEnd(p.id, e) } : {}}
                  >
                    <Popup>
                      <div style={{ fontSize: '12px' }}>
                        <b>{p.kode}</b><br />
                        {p.lokasi}<br />
                        Akurasi GPS: ~{p.akurasi.toFixed(0)}m
                        {pending && <><br /><span style={{ color: '#2E6DA4', fontWeight: 700 }}>Belum disimpan</span></>}
                      </div>
                    </Popup>
                  </Marker>
                </React.Fragment>
              );
            })}
          </MapContainer>
        )}
      </div>
      <p className="text-[11px] text-gray-400 mt-2">
        Area yang tidak tertutup lingkaran manapun berpotensi menjadi titik buta proteksi APAR. Klik titik untuk detail unit.
      </p>
    </div>
  );
}
